import { useEffect, useState, useCallback } from 'react';

import { removeDuplicates } from 'utils/removeDuplicates';
import { useToast } from 'hooks/useToast';
import type { Toast as ToastItem } from 'utils/createToast';

import errorIcon from 'assets/icons/error-icon.svg';
import loadingIcon from 'assets/icons/spinner.svg';
import checkCircleIcon from 'assets/icons/check-circle.svg';
import closeIcon from 'assets/icons/close.svg';

import {
  StyledToast,
  StyledIconAction,
  StyledCloseBtn,
  StyledToastContainer,
  StyledToastSeparator,
  StyledToastColumn,
} from 'components/Toast/styled';

const DISMISS_TIME = 5000;

const iconByType = (type?: string) => {
  switch (type) {
    case 'loading':
      return loadingIcon;
    case 'success':
      return checkCircleIcon;
    case 'error':
      return errorIcon;
    default:
      return null;
  }
};

// Toast dedicado ao outbox — a lista aqui é SEMPRE a do ToastContext global,
// que existe exclusivamente para sinalizar upload em segundo plano.
const OutboxToast = () => {
  const { toastList: contextToastList, setToastList } = useToast();
  const [list, setList] = useState<ToastItem[]>([]);

  useEffect(() => {
    setList(removeDuplicates(contextToastList || [], 'id'));
  }, [contextToastList]);

  const removeToasts = useCallback(
    (ids: Set<string | number>) => {
      setList(prev => prev.filter(t => !ids.has(t.id)));
      if (setToastList) {
        setToastList(prev => prev.filter(t => !ids.has(t.id)));
      }
    },
    [setToastList],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (!list.length) return;

      const now = Date.now();
      const expiredIds = list
        .filter(toast => {
          if (toast.type === 'loading') return false;
          if (toast.autoDelete === false) return false;
          return now - (toast.createdAt ?? 0) >= DISMISS_TIME;
        })
        .map(toast => toast.id);

      if (!expiredIds.length) return;
      removeToasts(new Set(expiredIds));
    }, DISMISS_TIME);

    return () => clearInterval(interval);
  }, [list, removeToasts]);

  return (
    <StyledToastContainer>
      {list.map((toast, i) => (
        <StyledToast key={toast.id || i} theme={toast.type}>
          <StyledToastColumn>
            {iconByType(toast.type) && (
              <StyledIconAction src={iconByType(toast.type) as string} alt={toast.type} type={toast.type} />
            )}
            <p>{toast.message}</p>
          </StyledToastColumn>

          <StyledToastSeparator />

          <StyledToastColumn>
            {toast.id !== 'outbox-pending' && (
              <StyledCloseBtn src={closeIcon} alt={toast.type} onClick={() => removeToasts(new Set([toast.id]))} />
            )}
          </StyledToastColumn>
        </StyledToast>
      ))}
    </StyledToastContainer>
  );
};

export { OutboxToast };
